"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import darkTheme from "@/themes/darkmode/theme";
import lightTheme from "@/themes/default/theme";

interface AppThemeContextProps {
  isDarkMode: boolean;
  muiTheme: typeof darkTheme | typeof lightTheme;
  toggleTheme: () => void;
}

/**
 * Determines the theme to use for the app.
 */
const AppThemeContext = createContext<AppThemeContextProps | undefined>(
  undefined,
);

export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  const [themeIndex, setThemeIndex] = useState(0); // Default to dark (0) for SSR

  // Wait until after initial hydration to load the theme, so that we don't cause a mismatch
  // between the server and client if the server is 0 (dark) and client is 1 (light).
  useEffect(() => {
    const saved = localStorage.getItem("themeIndex");
    if (saved !== null) {
      setThemeIndex(parseInt(saved, 10));
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeIndex((prev) => {
      const newValue = prev === 0 ? 1 : 0;
      localStorage.setItem("themeIndex", newValue.toString());
      return newValue;
    });
  }, []);

  return (
    <AppThemeContext.Provider
      value={{
        toggleTheme,
        isDarkMode: themeIndex === 0,
        muiTheme: themeIndex === 0 ? darkTheme : lightTheme,
      }}
    >
      {children}
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error("useAppTheme must be used within an AppThemeProvider");
  }
  return context;
};
